import { Util } from '@libs/utilities/util';

import { IPodDesign } from '../contracts/pod-design.interface';
import { IPodUpload } from '../contracts/pod-uploads.interface';
import { EnumPlatform } from '../contracts/print-on-demand.constants';
import { DisplateExportService, IDisplateXmlData } from './displate-export.service';
import { GlobalExportService, IGlobalXmlData } from './global-export.service';
import { IMbaXmlData, MbaExportService } from './mba-export.service';


/**------------------------------------------------------
 * Export POD data in the old XML-Format
 */
export class PodExportService {

	constructor(
		private globalExportService		: GlobalExportService,
		private mbaExportService		: MbaExportService,
		private displateExportService	: DisplateExportService
	) {}


	//** Exporting the pod data as xml */
	getFormattedPodData(data: IPodDesign): IPodXmlData {

		//0 - convert the listing data into the xml format
		const globalXmlData  : IGlobalXmlData   = this.globalExportService.getXmlFormattedData(data);
		const mbaXmlData	 : IMbaXmlData 		= this.mbaExportService.getXmlFormattedData(data);
		const displateXmlData: IDisplateXmlData = this.displateExportService.getXmlFormattedData(data);

		//1 - construct the formatted xml data
		const formattedData: IPodXmlData = {
			design_info : {
				meta : {
					date 			: Util.Date.format('MM/DD/YYYY'),
					time 			: Util.Date.format('HH:mm:ss'),
					note 			: '',
					copyright 		: 'Flying Upload',
					flyId			: data.image.flyId,
					'xml-version'	: '2.1'
				},
				design	: {
					...globalXmlData,					// global xml data
					platform_specifics : {
						displate 		: {
							...displateXmlData			// displate xml data
						},
						amazon_mba 		: {
							...mbaXmlData				// mba xml data
						}
					}
				},
				other_info : {
					uploaded_flags	: this.getUploadFlags(data)
				}
			}
		};

		return formattedData;
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private getUploadFlags(data: IPodDesign): string {

		//0 - are any uploads defined?
		const uploads: IPodUpload[] = data?.uploads?.uploads;
		if (Util.Basic.isUndefined(uploads)) return '';

		//1 - convert the uploads to the old format
		const oldUploadFlags: string[] = [];
		for (const upload of uploads) {

			//a. is the platform available?
			const oldFlag: string = this.PLATFORM_MAPPING[upload.platform];
			if (Util.String.isEmpty(oldFlag)) continue;

			//b. if mapping found add to old upload flags
			oldUploadFlags.push(oldFlag);
		}

		//2 - return the mapped upload flags for the old uploader
		const uploadFlagsString: string = (oldUploadFlags.length !== 0) ?  `${oldUploadFlags.join(', ')}, ` : '';		// the last "," is required for the old uploader
		return uploadFlagsString;
	}


	/**------------------------------------------------------
	 * Legacy Upload Flag Mapping
	 */
	private readonly PLATFORM_MAPPING: Record<EnumPlatform, string> = {
		[EnumPlatform.Printify] 		: 'Printify.com',
		[EnumPlatform.MBA] 				: 'Merch.Amazon.com',
		[EnumPlatform.Redbubble] 		: 'Redbubble.com',
		[EnumPlatform.SpreadshirtUS] 	: 'Spreadshirt.com',
		[EnumPlatform.SpreadshirtEU] 	: 'Spreadshirt.eu',
		[EnumPlatform.Teepublic] 		: 'Teepublic.com',
		[EnumPlatform.Society6] 		: 'Society6.com',
		[EnumPlatform.Teespring] 		: 'Teespring.com',
		[EnumPlatform.Zazzle] 			: 'Zazzle.com',
		[EnumPlatform.Displate] 		: 'Displate.com',
		[EnumPlatform.Printful] 		: 'Printful.com',
		[EnumPlatform.Shirtee] 			: 'Shirtee.com',
		[EnumPlatform.FineArtAmerica] 	: 'FineArtAmerica.com',
		[EnumPlatform.Tostadora] 		: 'Tostadora.com',
		[EnumPlatform.KDP] 				: 'kdp.Amazon.com'
	};
}


//** Interfaces --------------------------------- */
export interface IPodXmlData {
	design_info : {
		meta		: {
			date 		 : string;
			time 		 : string;
			note 		 : string;
			copyright 	 : string;
			flyId		 : string;
			'xml-version': string;
		};
		design		: IGlobalXmlData & IPlatformSpecificXmlData;
		other_info 	: {
			uploaded_flags: string;
		};
	};
}

interface IPlatformSpecificXmlData {
	platform_specifics 	: {
		displate 		: IDisplateXmlData;
		amazon_mba 		: IMbaXmlData;
	};
}
